///////////////////////////////////////////////////////////////////////////////
// /checkout — guest-first checkout for the new web app: Ding mobile topups
// and eSIM data plans share one page.
//
// No login required: an unrecognised email quietly gets a passwordless
// account (POST /api/checkout/guest) so the existing, session-bound payment
// routes can run. A recognised email is never taken over silently — the page
// offers to sign in instead.
//
// Prices come from the server (/api/checkout/quote for topups,
// /api/esims/quote for plans), never from arithmetic here.
///////////////////////////////////////////////////////////////////////////////
import React, { useEffect, useMemo, useRef, useState } from 'react';
import posthog from 'posthog-js';
import { Header, ST, PAYMENT_LOGOS, PHP_PAYMENT_LOGOS, countryName, fmtBaht, fmtMoney, bfp } from './Chrome';
import ALL_COUNTRIES from '../public/lib/countries.json';
import { SLUG_BY_ISO } from '../../lib/countrySlugs';
import { ReferralPromoCard } from './Referrals';
import { formatExpiry, parseExpiry } from './cardExpiry';
import { SiteFooterV2 } from './Footers';

const MT = (typeof window !== 'undefined' && window.__MT) || {};

// Omise needs a billing address for these; everywhere else the card is enough.
const ADDRESS_COUNTRIES = ['US', 'CA', 'GB'];

// Shown first; every other country follows A-Z so any cardholder can pick
// their own (unlisted buyers used to fall back to US and a US address).
const COMMON_CARD_COUNTRIES = [
  'US', 'CA', 'GB', 'TH', 'AU', 'SG', 'MY', 'PH', 'ID', 'VN', 'JP', 'KR', 'IN',
  'DE', 'FR', 'IT', 'ES', 'NL', 'SE', 'NO', 'DK', 'CH', 'IE', 'NZ', 'AE',
];
const OTHER_CARD_COUNTRIES = ALL_COUNTRIES.map((c) => c.code)
  .filter((c) => !COMMON_CARD_COUNTRIES.includes(c))
  .sort((a, b) => countryName(a).localeCompare(countryName(b)));
const CARD_COUNTRIES = [...COMMON_CARD_COUNTRIES, ...OTHER_CARD_COUNTRIES];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN',
  'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH',
  'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT',
  'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

// `mark` stands in for methods with no logo of their own.
const METHOD_META = {
  omise: { label: 'Card', logos: ['visa.png', 'master.png', 'jcb.png'], cur: 'THB' },
  'omise-tt': { label: 'Card', logos: ['visa.png', 'master.png', 'jcb.png'], cur: 'THB' },
  'stripe-crypto': { label: 'USDC', mark: '$', cur: 'USD', paylogos: ['ethereum.svg', 'arbitrum.svg', 'base.svg'] },
  // The only eSIM rail that charges in the currency shown, rather than Baht.
  'stripe-esim': { label: 'Card', logos: ['visa.png', 'master.png', 'amex.png', 'jcb.png'], cur: 'USD' },
  // Payment Links+ pages only a Thai bank account or wallet can pay — say so
  // on the tile; outside Thailand the server also flags them `regional` (#291).
  'thai-wallets': { label: 'Thai Wallets', logos: ['linepay.svg', 'truemoney.svg', 'kbank.png', 'scb.png'], cur: 'THB', sub: 'PromptPay · TrueMoney · Thai mobile banking' },
  'thai-bank': { label: 'Thai bank', logos: ['kbank.png', 'scb.png'], cur: 'THB', sub: 'Thai bank account required' },
  alipay: { label: 'Alipay', logos: ['alipay.png'], cur: 'THB' },
  // Named for what the Xendit invoice takes, not the currency it is priced
  // in: customers quoted in pesos picked "Philippine Peso", landed on a page
  // with no card and the invoice expired unpaid (#279).
  xendit: { label: 'Maya · ShopeePay · GrabPay', logos: ['paymaya-logo.svg', 'shopeepay-logo.svg', 'bpi-logo.svg', 'ubp-logo.svg'], sub: 'Philippine wallet or bank only · no card' },
  balance: { label: 'Balance', mark: '฿', cur: 'THB' },
  balance_usd: { label: 'USD Wallet', mark: '$', cur: 'USD' },
};

const TRUST = ['SSL secured', 'PCI compliant', 'Verified merchant', '24/7 support'];

// Four products check out here: a Ding topup (phone + sku), an international
// Reloadly topup (phone + operator, type=intl), a Wondd Thailand topup
// (phone + carrier + amount, type=wondd) and an eSIM plan (package code).
// `id` alone means we're back from a 3DS card page.
// `3ds=topup` means it's a topup 3DS return (not eSIM).
function readParams() {
  const q = new URLSearchParams(window.location.search);
  const get = (k) => q.get(k) || '';
  const code = get('code');
  const returnId = get('id');
  const is3dsTopup = get('3ds') === 'topup';
  const xenditTopup = get('xendit') === 'topup';
  const xenditDeposit = get('xendit') === 'deposit';
  const stripeReturn = get('stripe') === 'success' || get('stripe') === 'cancel';
  // Stripe's own "back" link: the page was left without paying (#299)
  const stripeCancel = get('stripe') === 'cancel';
  return {
    kind: (is3dsTopup || xenditTopup) ? 'topup'
      : (get('type') === 'esim' || code || returnId) ? 'esim'
      : (get('type') === 'dd' || get('type') === 'wondd') ? 'wondd'
      : get('type') === 'gift' ? 'gift'
      : (get('type') === 'intl' || get('operator')) ? 'intl' : 'topup',
    isGift: get('type') === 'gift',
    phone: get('phone'),
    iso: get('iso').toUpperCase() || ((get('type') === 'dd' || get('type') === 'wondd') ? 'TH' : ''),
    provider: get('provider'),
    providerName: get('providerName') || get('provider'),
    sku: get('sku'),
    operator: get('operator'),
    carrier: get('carrier'),
    value: get('value') || get('amount'),
    validity: get('validity') === '1',
    code,
    days: get('days'),
    returnId,
    is3dsTopup,
    xenditTopup,
    xenditDeposit,
    stripeReturn,
    stripeCancel,
    currency: get('currency').toUpperCase(),
    // `method=card` (or a method code) pre-selects a rail: "Pay with card
    // instead" after an expired Xendit invoice lands here (#279).
    method: get('method').toLowerCase(),
    // Prefill for signed-out buyers (AI-agent checkout links, /a/<id>). Editable.
    email: get('email').trim(),
  };
}

function apiError(data, fallback) {
  const e = data && data.error;
  return (e && (e.errorMessage || e.message)) || (data && data.message) || fallback;
}

// Antifraud purchasing-cap rejections (lib/antifraud.coffee). The server
// message is already worded ("This number has reached its monthly top-up limit
// of 3,801 THB (3,372 THB used ...). You can top up 800 THB again from 17 Oct,
// 08:00 UTC."); `details` lets an older server message still get a reset date.
const LIMIT_CODES = new Set([
  'account_reached_transaction_limit',
  'account_reached_transaction_amount_limit',
  'account_reached_limit',
]);

function limitErrorText(err) {
  const d = err.details;
  let text = err.message || 'You have reached your purchasing limit.';
  if (d && d.resetAt && !/again from/.test(text)) {
    const when = new Date(d.resetAt);
    if (!Number.isNaN(when.getTime())) {
      text += ` You can top up again from ${when.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}.`;
    }
  }
  return text;
}

async function postJson(url, body) {
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(apiError(data, `HTTP ${r.status}`));
    err.code = (data && data.error && data.error.code) || null;
    err.status = r.status;
    // Purchasing-limit rejections (#278) carry the cap, usage, reset date and a
    // help link; keep them so the notice can say more than the sentence.
    err.details = (data && data.error && data.error.details) || null;
    err.learnMore = (data && data.error && data.error.learnMore) || null;
    throw err;
  }
  return data;
}

// Turnstile: renders the managed widget and provides the current token.
// Falls back to reCAPTCHA v3 if Turnstile is not configured.
function useTurnstile(containerId) {
  const [token, setToken] = React.useState(null);
  const widgetIdRef = React.useRef(null);
  const rendered = React.useRef(false);

  React.useEffect(() => {
    const siteKey = MT.turnstileSiteKey;
    if (!siteKey || rendered.current) return;

    function render() {
      if (!window.turnstile || rendered.current) return;
      const el = document.getElementById(containerId);
      if (!el) return;
      rendered.current = true;
      widgetIdRef.current = window.turnstile.render(el, {
        sitekey: siteKey,
        callback: (t) => setToken(t),
        'expired-callback': () => setToken(null),
        'error-callback': () => setToken(null),
        appearance: 'interaction-only',
      });
    }

    if (window.turnstile) { render(); return; }
    const iv = setInterval(() => { if (window.turnstile) { clearInterval(iv); render(); } }, 200);
    return () => clearInterval(iv);
  }, [containerId]);

  const reset = () => {
    setToken(null);
    if (widgetIdRef.current != null && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  };

  return { token, reset };
}

function recaptchaToken(action) {
  return new Promise((resolve) => {
    const key = MT.recaptchaKey;
    if (!key || !window.grecaptcha || !window.grecaptcha.ready) return resolve(null);
    try {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(key, { action }).then(resolve).catch(() => resolve(null));
      });
    } catch (e) {
      resolve(null);
    }
  });
}

// /api/esims/quote/:code speaks its own dialect — reshape it into the topup
// quote's fields so the page renders one way.
function normalizeEsimQuote(q) {
  return {
    esim: true,
    plan: {
      name: q.name,
      data_label: q.data_label,
      duration: q.duration,
      duration_unit: q.duration_unit,
      is_daily: q.is_daily,
      days: q.days,
      slug: q.slug,
    },
    usd_rate: q.usd_rate,
    price_usd: q.price_usd,
    topup_thb: Math.round(q.thb_base * 100) / 100,
    display: q.display,
    fees_waived: false,
    methods: (q.methods || [])
      .filter((m) => m.enabled)
      .map((m) => ({
        code: m.code,
        label: m.label,
        fee_thb: m.fee_thb,
        fee_is_minimum: m.fee_is_minimum,
        total_thb: m.total_thb,
        display: m.display,
        settles_in: m.settles_in,
        settlement_total: m.settlement_total,
        balance_thb: m.balance_thb,
        discount: m.discount,
        regional: m.regional,
      })),
  };
}

// Human-readable messages for Omise failure codes returned at tokenization.
const OMISE_ERRORS = {
  failed_fraud_check: 'Your card was declined by the payment processor.',
  invalid_card_number: 'The card number is invalid. Check for typos.',
  invalid_expiration_date: 'The expiration date is invalid.',
  invalid_security_code: 'The security code (CVV) is invalid.',
  expired_card: 'This card has expired.',
  stolen_or_lost_card: 'This card has been reported lost or stolen.',
  insufficient_fund: 'Insufficient funds or credit limit reached.',
  payment_rejected: 'Payment rejected by the issuing bank.',
  failed_processing: 'Card processing failed. Check your details and try again.',
};

function omiseTokenWith(publicKey, card) {
  return new Promise((resolve, reject) => {
    if (!window.Omise) return reject(new Error('Card check failed. Disable script blockers and reload.'));
    if (!publicKey) return reject(new Error('Card payments are unavailable right now.'));
    window.Omise.setPublicKey(publicKey);
    window.Omise.createToken('card', card, (statusCode, token) => {
      if (statusCode === 200 && token && token.object === 'token' && token.id) return resolve(token);
      const code = token && token.code;
      const friendly = (code && OMISE_ERRORS[code]) || null;
      const err = new Error(friendly || 'Your card was declined. Check your details or try a different card.');
      err.omiseCode = code || (token && token.message) || 'unknown';
      reject(err);
    });
  });
}
function omiseToken(card) { return omiseTokenWith(MT.omisePublicKey, card); }
function omiseTokenTT(card) { return omiseTokenWith(MT.omiseTTPublicKey, card); }

// Browser PostHog may be absent (opted out, blocked); telemetry never throws.
function track(event, props) {
  try { if (posthog.__loaded) posthog.capture(event, props); } catch (e) { /* ignore */ }
}

export default function CheckoutPage({ me, onMe, currency, onCurrency }) {
  const params = useMemo(readParams, []);
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [method, setMethod] = useState('omise');
  const methodAutoSelected = useRef(false);
  // Thai-only rails (quoted `regional: true` outside Thailand) stay folded
  // behind "Paying from a Thai bank or wallet?" until asked for (#291).
  const [showRegional, setShowRegional] = useState(false);

  // identity
  const [email, setEmail] = useState(params.email);
  const [identity, setIdentity] = useState(null); // { known, can_login }
  const [password, setPassword] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [linkSent, setLinkSent] = useState(false);

  // card — most people pay with a card issued where they're topping up, so
  // start there and only ask for an address when Omise needs one.
  const [card, setCard] = useState({
    first: '', last: '', country: CARD_COUNTRIES.includes(params.iso) ? params.iso : 'US',
    street: '', city: '', state: '', postal: '', number: '', exp: '', cvv: '',
  });

  const turnstile = useTurnstile('turnstile-checkout');

  const [agree, setAgree] = useState(() => {
    try { return localStorage.getItem('mt_tos_agreed') === '1'; } catch (e) { return false; }
  });
  const [placing, setPlacing] = useState(false);
  const [payError, setPayError] = useState(null);
  const [payErrorMore, setPayErrorMore] = useState(null); // "Learn more" link for limit errors (#278)
  const [cardLinkUrl, setCardLinkUrl] = useState(null); // Omise hosted checkout fallback
  const [cardLinkBusy, setCardLinkBusy] = useState(false);
  const [done, setDone] = useState(null); // completed topup order
  // Topup order tracking: stages are payment → sending → processing → success/failed
  const isTopupReturn = (params.is3dsTopup || params.xenditTopup) && params.returnId;
  const [topupStage, setTopupStage] = useState(isTopupReturn ? 'processing' : null);
  const [topupOrderId, setTopupOrderId] = useState(isTopupReturn ? params.returnId : null);
  // A card decline (nothing charged) is told apart from a top-up that failed
  // after payment (refunded): { code, message } from the receipt, else null (#290)
  const [payFailure, setPayFailure] = useState(null);
  // How the failed order was being paid and where to pay the same product
  // again with a card, both from the receipt (#279)
  const [failedMethod, setFailedMethod] = useState(null);
  const [retryUrl, setRetryUrl] = useState(null);
  // eSIM delivery happens after payment, so it has its own little state machine
  const isEsimReturn = params.returnId && !params.is3dsTopup && !params.xenditTopup && !params.xenditDeposit;
  const [esimOrderId, setEsimOrderId] = useState(isEsimReturn ? params.returnId : null);
  // Stripe returns skip the Omise 3DS verify step and go straight to polling.
  // Back from Stripe's page without paying is not a payment: 'unpaid' shows
  // the decline (if any) and a way to try again instead of waiting on an
  // eSIM that was never bought (#299).
  const [esimStage, setEsimStage] = useState(
    isEsimReturn ? (params.stripeCancel ? 'unpaid' : params.stripeReturn ? 'provisioning' : 'verifying') : null
  );
  const [esimOrder, setEsimOrder] = useState(null);
  const emailRef = useRef(null);
  // The account wall's actionable control (password box, or the sign-in-link
  // button for a password-less guest). "sign in to continue" used to be plain
  // text beside Place order that got rage-clicked while the control sat up at
  // the email field; both wall paths now scroll to and focus it (#312).
  const knownRef = useRef(null);
  const focusKnownPending = useRef(false);
  function focusKnownBox() {
    const el = knownRef.current;
    if (!el) { focusKnownPending.current = true; return; }
    focusKnownPending.current = false;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.focus({ preventScroll: true });
  }
  useEffect(() => {
    if (focusKnownPending.current && knownRef.current) focusKnownBox();
  }, [identity]);
  const wallMessage = (canLogin) => (canLogin
    ? 'That email already has an account — enter your password above to continue.'
    : 'That email already has an account — use the sign-in link above to continue.');

  const signedIn = !!me;
  const [usdBalance, setUsdBalance] = useState(null);

  // Saved Omise cards — extracted from the user's Omise Customer object.
  // null = use new card form; a card id = charge that saved card.
  const savedCards = useMemo(() => {
    const cust = me && me.omiseCustomer;
    if (!cust || !cust.cards || !cust.cards.data || !cust.cards.data.length) return [];
    return cust.cards.data
      .filter((c) => c && c.id && !c.deleted)
      .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  }, [me && me.omiseCustomer]);
  const [useSavedCard, setUseSavedCard] = useState(null); // card id or null

  useEffect(() => {
    if (me && me.email) setEmail(me.email);
  }, [me && me.email]);

  // A prefilled email is never blurred, so check it once up front: an
  // existing account gets the sign-in prompt now, not a 409 at Pay.
  useEffect(() => {
    if (params.email && !me) checkEmail(params.email);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Trustpilot review invitation — fire once on order success (topup or eSIM)
  const tpSentRef = useRef(false);
  useEffect(() => {
    if (tpSentRef.current) return;
    const isSuccess = topupStage === 'success' || esimStage === 'ready';
    if (!isSuccess) return;
    const recipientEmail = (me && me.email) || email;
    if (!recipientEmail) return;
    const orderId = topupOrderId || esimOrderId;
    const ref = orderId ? 'ORD-' + orderId.replace(/-/g, '').slice(0, 8) : undefined;
    tpSentRef.current = true;
    if (typeof tp === 'function') {
      tp('register', 'FMkPmXoxgvStX3jp');
      tp('createInvitation', {
        recipientEmail,
        recipientName: me ? [me.firstName, me.lastName].filter(Boolean).join(' ') : '',
        referenceId: ref,
        source: 'InvitationScript',
      });
    }
  }, [topupStage, esimStage]);

  // Auto-select the first saved card when user signs in, pre-fill billing from most recent
  // (not for omise-tt — TT is a separate Omise account with its own customer/cards)
  useEffect(() => {
    if (savedCards.length > 0 && useSavedCard === null && method !== 'omise-tt') {
      const cust = me && me.omiseCustomer;
      setUseSavedCard((cust && cust.default_card) || savedCards[0].id);
      var recent = savedCards[0];
      if (recent && recent.name) {
        var nameParts = (recent.name || '').split(/\s+/);
        setCard((prev) => ({
          ...prev,
          first: nameParts[0] || prev.first,
          last: nameParts.slice(1).join(' ') || prev.last,
          street: recent.street1 || prev.street,
          city: recent.city || prev.city,
          state: recent.state || prev.state,
          postal: recent.postal_code || prev.postal,
          country: (recent.country || prev.country).toUpperCase(),
        }));
      }
    }
  }, [savedCards.length]);

  // Saved cards live on the MT Omise customer, so they only apply to 'omise'.
  // The quote can auto-switch to 'omise-tt' after a card was pre-selected
  // (#229); without this the TT route gets an MT card id -> card_not_found.
  const savedCardId = method === 'omise' ? useSavedCard : null;

  // Fetch USD balance for wallet payment option
  useEffect(() => {
    if (!me || !me.hasUsdWallet) return;
    fetch('/api/wallet/balances')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setUsdBalance(d.usd || 0); })
      .catch(() => {});
  }, [me && me.hasUsdWallet]);

  // Quote — re-fetched when the session changes because balance and any
  // waived fees depend on who is asking. Both product kinds are normalised to
  // one shape so everything below this point is product-agnostic.
  useEffect(() => {
    if (params.returnId) return undefined; // 3DS return: nothing to price
    const isEsim = params.kind === 'esim';
    const isIntl = params.kind === 'intl';
    const isWondd = params.kind === 'wondd';
    const isGift = params.kind === 'gift';
    const missing = isEsim ? !params.code
      : isWondd ? (!params.carrier || !params.value)
      : isIntl ? (!params.operator || !params.iso || !params.value || params.value === 'null')
      : isGift ? (!params.sku || !params.iso)
        : (!params.sku || !params.iso || !params.provider);
    if (missing) {
      setQuoteError('This checkout link is missing its product.');
      return undefined;
    }
    let dead = false;

    const url = (() => {
      const qs = new URLSearchParams();
      if (params.currency || currency) qs.set('currency', params.currency || currency);
      if (isEsim) {
        if (params.days) qs.set('days', params.days);
        return `/api/esims/quote/${encodeURIComponent(params.code)}?${qs.toString()}`;
      }
      if (isWondd) {
        qs.set('carrier', params.carrier);
        qs.set('amount', params.value);
        if (params.validity) qs.set('validity', '1');
        return `/api/checkout/quote-wondd?${qs.toString()}`;
      }
      if (isGift) {
        qs.set('iso', params.iso);
        qs.set('sku', params.sku);
        return `/api/checkout/quote-gift?${qs.toString()}`;
      }
      if (isIntl) {
        qs.set('iso', params.iso);
        qs.set('operator', params.operator);
        qs.set('value', params.value);
        return `/api/checkout/quote-intl?${qs.toString()}`;
      }
      qs.set('iso', params.iso);
      qs.set('provider', params.provider);
      qs.set('sku', params.sku);
      // the amount was chosen on /recharge; checkout only prices it
      if (params.value) qs.set('value', params.value);
      return `/api/checkout/quote?${qs.toString()}`;
    })();

    fetch(url, { credentials: 'same-origin' })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(apiError(data, isEsim ? 'Could not price this plan.' : 'Could not price this topup.'));
        return data;
      })
      .then((q) => {
        if (dead) return;
        const normalized = isEsim ? normalizeEsimQuote(q) : q;
        setQuote(normalized);
        setQuoteError(null);
        // Auto-select a method on the initial quote load only — a re-fetch
        // after login must not stomp a method the customer already chose.
        //
        // Card first. Picking by settlement currency put every USD-display
        // visitor on USDC, the only USD-settling rail on a top-up, which pays
        // 1 order in 6 while card is the one method international customers
        // complete (#285). The display total is already converted, so a USD
        // visitor does not need a USD-settling method to see "$20.30". Among
        // cards the one in the display currency still wins (eSIM offers both
        // a THB and a USD card). Balance is never the default; USDC is opt-in.
        if (!methodAutoSelected.current) {
          const dispQuoted = normalized.display && normalized.display.currency;
          // Euros and pounds are display-only: the card to prefer is the USD one.
          const disp = dispQuoted === 'EUR' || dispQuoted === 'GBP' ? 'USD' : dispQuoted;
          // A folded regional rail is never the default either (#291).
          const offered = (normalized.methods || []).filter((m) => !m.code.startsWith('balance') && !m.regional);
          const cards = offered.filter((m) => m.code === 'omise' || m.code === 'omise-tt' || m.code === 'stripe-esim');
          // A retry link asks for a rail up front: `card`, or a method code (#279)
          const asked = params.method === 'card'
            ? (cards.find((m) => m.settles_in === disp) || cards[0])
            : offered.find((m) => m.code === params.method);
          const match = asked
            || cards.find((m) => m.settles_in === disp)
            || cards[0]
            || offered.find((m) => m.code !== 'stripe-crypto')
            || offered[0]
            || (normalized.methods || [])[0];
          if (match) setMethod(match.code);
          methodAutoSelected.current = true;
        }
      })
      .catch((err) => { if (!dead) setQuoteError(err.message); });
    return () => { dead = true; };
  }, [params.kind, params.code, params.days, params.sku, params.iso, params.provider, params.operator, params.carrier, params.value, currency, me && me.id]);

  // Back from the 3DS page: settle the charge, then watch for delivery.
  // The Omise webhook settles it too, so this is belt and braces.
  useEffect(() => {
    if (esimStage !== 'verifying' || !esimOrderId) return;
    // A bare ?id= is not always an eSIM: Payment Links+ returns (retry
    // emails, older card-link / thai-wallets / alipay links, the app's
    // Alipay) bring top-up orders here too. The public receipt says which
    // table the id is in; a top-up goes to the top-up tracker (#153).
    fetch(`/api/receipt/${encodeURIComponent(esimOrderId)}`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
      .then((o) => {
        if (o && o.type === 'topup') {
          setEsimStage(null);
          setEsimOrderId(null);
          setTopupOrderId(o.id);
          if (o.status === 'failed' || o.status === 'refunded') failFromReceipt(o);
          else setTopupStage('processing');
          return;
        }
        verifyEsim();
      });
  }, [esimStage, esimOrderId]);

  function verifyEsim() {
    postJson(`/api/payment/esim/verify/${encodeURIComponent(esimOrderId)}`)
      .then((d) => {
        const status = d.order && d.order.status;
        if (status === 'failed' || status === 'refunded') setEsimStage('failed');
        else setEsimStage('provisioning');
      })
      .catch((err) => {
        setPayError(err.message);
        setEsimStage('provisioning'); // the webhook may still land it
      });
  }

  // Poll delivery — the eSIM is provisioned after payment, not during.
  useEffect(() => {
    if (esimStage !== 'provisioning' || !esimOrderId) return undefined;
    let tries = 0;
    let last = null; // newest order the poll saw: decides what "took too long" means (#317)
    const t = setInterval(() => {
      tries += 1;
      if (tries > 18) { // ~45s
        clearInterval(t);
        // Paid and still provisioning is "taking longer". Never paid is not a
        // delay at all: the buyer is still at, or left, the payment page.
        setEsimStage(last && last.status !== 'paid' ? 'awaiting' : 'delayed');
        return;
      }
      fetch(`/api/esims/order/${encodeURIComponent(esimOrderId)}`, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : null))
        .then((o) => {
          if (!o) return;
          last = o;
          setEsimOrder(o);
          if (o.purchase_status === 'success') { setEsimStage('ready'); clearInterval(t); }
          if (o.purchase_status === 'failed' || o.status === 'refunded') { setEsimStage('failed'); clearInterval(t); }
          // failed before any purchase: the Stripe page expired or declined (#299)
          if (o.status === 'failed' && !o.purchase_status) { setEsimStage('unpaid'); clearInterval(t); }
        })
        .catch(() => {});
    }, 2500);
    return () => clearInterval(t);
  }, [esimStage, esimOrderId]);

  // Unpaid return: read the order once for the decline reason; if the webhook
  // landed a payment after all, carry on as a normal paid return.
  // Stripe's own "back" link lands here as stripe=cancel: tell the server,
  // which expires the Stripe page and fails the order as cancelled instead of
  // leaving it open for the rest of the half hour (#317). One POST per page
  // load (StrictMode double-mount); if it fails, the order is simply read.
  const cancelSentRef = useRef(false);
  useEffect(() => {
    if (esimStage !== 'unpaid' || !esimOrderId) return undefined;
    let dead = false;
    const readOrder = () => fetch(`/api/esims/order/${encodeURIComponent(esimOrderId)}`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null));
    let read;
    if (params.stripeCancel && !cancelSentRef.current) {
      cancelSentRef.current = true;
      read = postJson(`/api/payment/esim/cancel/${encodeURIComponent(esimOrderId)}`)
        // `paid`: Stripe already holds a payment for it (another tab, late
        // webhook) - not a cancel, poll it like a paid return
        .then((d) => (d && d.order ? { ...d.order, paid_elsewhere: !!d.paid } : null))
        .catch(() => readOrder());
    } else {
      read = readOrder();
    }
    read
      .then((o) => {
        if (dead || !o) return;
        setEsimOrder(o);
        if (o.status === 'paid' || o.purchase_status || o.paid_elsewhere) setEsimStage('provisioning');
      })
      .catch(() => {});
    return () => { dead = true; };
  }, [esimStage, esimOrderId]);

  // Delayed state: show support link after extra wait
  const [showSupport, setShowSupport] = useState(false);
  useEffect(() => {
    if (esimStage !== 'delayed') { setShowSupport(false); return undefined; }
    const t = setTimeout(() => setShowSupport(true), 120000); // 2 min after entering delayed
    return () => clearTimeout(t);
  }, [esimStage]);

  // "Check Status" handler — single poll from the delayed state
  const [checking, setChecking] = useState(false);
  function checkEsimStatus() {
    if (!esimOrderId || checking) return;
    setChecking(true);
    fetch(`/api/esims/order/${encodeURIComponent(esimOrderId)}`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((o) => {
        setChecking(false);
        if (!o) return;
        setEsimOrder(o);
        if (o.purchase_status === 'success') setEsimStage('ready');
        else if (o.purchase_status === 'failed' || o.status === 'refunded') setEsimStage('failed');
        else if (o.status === 'failed') setEsimStage('unpaid'); // expired, declined or cancelled before any purchase (#299)
        else if (o.status === 'paid') setEsimStage('provisioning'); // restart polling
        else setEsimStage('awaiting'); // still no payment: not "received safely" (#317)
      })
      .catch(() => setChecking(false));
  }

  // 3DS topup return: settle the charge, then let the processing poll take over.
  // Xendit topup returns skip this — the IPN webhook settles the charge.
  useEffect(() => {
    if (!params.returnId) return;
    if (params.xenditTopup) return; // Xendit: webhook settles it, just poll
    if (!params.is3dsTopup) return;
    // The receipt carries the decline reason (#290), so a failed order is
    // always read from there.
    const checkReceiptOnce = () => fetch(`/api/receipt/${encodeURIComponent(params.returnId)}`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((o) => {
        if (o && (o.status === 'failed' || o.status === 'refunded')) failFromReceipt(o);
      })
      .catch(() => {});
    postJson(`/api/payment/omise/process/${encodeURIComponent(params.returnId)}`)
      .then((d) => {
        if (d && d.order && (d.order.status === 'failed' || d.order.status === 'refunded')) checkReceiptOnce();
        // otherwise stay in 'processing' — the poll effect will pick up the result
      })
      .catch(() => {
        // No webhook follows a synchronous Omise decline (#270), and a Payment
        // Links+ order (card-link / thai-wallets / alipay) has no 3DS charge to
        // process: look at the receipt once so a failed order shows the decline
        // instead of polling into "taking longer than usual".
        checkReceiptOnce();
      });
  }, []);

  // Receipt says the order is failed/refunded. No topup_status means the money
  // was never taken (declined card, expired payment page, refused order); a
  // topup_status means the top-up itself failed after payment and is refunded.
  function failFromReceipt(o) {
    if (o && o.status === 'failed' && !o.topup_status) {
      setPayFailure(o.payment_failure || { code: 'failed', message: null });
      setFailedMethod(o.method || null);
      setRetryUrl(o.retry_url || null);
    }
    setTopupStage('failed');
  }

  // Topup order tracking — animate through stages, then poll for completion
  useEffect(() => {
    if (!topupStage || !topupOrderId) return undefined;
    if (topupStage === 'success' || topupStage === 'failed' || topupStage === 'delayed') return undefined;

    // Stage progression timers: payment → sending → processing (then poll takes over)
    if (topupStage === 'payment') {
      const t = setTimeout(() => setTopupStage('sending'), 1800);
      return () => clearTimeout(t);
    }
    if (topupStage === 'sending') {
      const t = setTimeout(() => setTopupStage('processing'), 2200);
      return () => clearTimeout(t);
    }

    // 'processing' — poll the receipt endpoint for final status
    let tries = 0;
    const t = setInterval(() => {
      tries += 1;
      if (tries > 15) { clearInterval(t); setTopupStage('delayed'); return; } // ~45s without result
      fetch(`/api/receipt/${encodeURIComponent(topupOrderId)}`, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : null))
        .then((o) => {
          if (!o) return;
          if (o.topup_status === 'SUCCESS') {
            setTopupStage('success');
            setDone(o);
            clearInterval(t);
          } else if (o.topup_status === 'FAILED' || o.status === 'failed' || o.status === 'refunded') {
            failFromReceipt(o);
            clearInterval(t);
          }
        })
        .catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [topupStage, topupOrderId]);

  // Deposit return: the URL alone doesn't prove the deposit settled (#281).
  // Poll the public receipt until the deposit is fulfilled or failed, then
  // say so; after ~45 s without an answer, say it is still processing.
  const isDepositReturn = !!(params.xenditDeposit && params.returnId);
  const [depositStage, setDepositStage] = useState(isDepositReturn ? 'checking' : null); // checking | credited | failed | pending
  const [depositInfo, setDepositInfo] = useState(null);
  useEffect(() => {
    if (depositStage !== 'checking') return undefined;
    let tries = 0;
    let stopped = false;
    const check = () => {
      tries += 1;
      fetch(`/api/receipt/${encodeURIComponent(params.returnId)}`, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : null))
        .then((o) => {
          if (stopped) return;
          if (o && o.type === 'deposit') {
            setDepositInfo(o);
            if (o.status === 'fulfilled') { clearInterval(t); setDepositStage('credited'); return; }
            if (o.status === 'failed' || o.status === 'canceled') { clearInterval(t); setDepositStage('failed'); return; }
          }
          if (tries >= 15) { clearInterval(t); setDepositStage('pending'); }
        })
        .catch(() => {
          if (!stopped && tries >= 15) { clearInterval(t); setDepositStage('pending'); }
        });
    };
    const t = setInterval(check, 3000);
    check();
    return () => { stopped = true; clearInterval(t); };
  }, [depositStage]);

  const isEsim = params.kind === 'esim';
  const cur = (quote && quote.display) || null;
  // Which currencies we can actually take money in for this order — one entry
  // per currency some available method settles in.
  const payCurrencies = quote
    ? [...new Set(quote.methods.filter((m) => !m.requires_login || signedIn).map((m) => m.settles_in))]
        .sort((a, b) => {
          // Put the user's display currency first
          const disp = cur && cur.currency;
          if (a === disp) return -1;
          if (b === disp) return 1;
          return 0;
        })
    : [];
  const showsThb = !cur || cur.currency === 'THB';
  // Amounts are quoted by the server in the customer's currency; THB stays the
  // settlement currency underneath.
  const money = (thbValue, displayValue) => (
    showsThb ? fmtBaht(thbValue) : fmtMoney(displayValue, cur.currency, cur.symbol)
  );
  const selected = quote && quote.methods.find((m) => m.code === method);
  // Regional rails unfold when asked, when one is already chosen, or when no
  // other rail can take the payment (a ฿10 Thai top-up sits below the card
  // floor, so Thai Wallets is all there is).
  const regionalMethods = quote ? quote.methods.filter((m) => m.regional) : [];
  // This customer has already had Xendit invoices expire unpaid this week:
  // say why and point at a card before it happens again (#279)
  const xenditMethod = quote ? quote.methods.find((m) => m.code === 'xendit') : null;
  const xenditRepeat = !!(xenditMethod && xenditMethod.recent_expiries >= 2);
  const regionalOpen = showRegional
    || !!(selected && selected.regional)
    || !(quote && quote.methods.some((m) => !m.regional && !m.code.startsWith('balance')));
  const balanceMethod = quote && quote.methods.find((m) => m.code === 'balance');
  const balanceShort = !!(signedIn && balanceMethod && Number(me.availableBalance || 0) < balanceMethod.total_thb);

  // A page that thinks it is signed out may be stale: Back from a hosted
  // payment page, or a /me that failed on a flaky connection (#305). Ask the
  // server again before re-asking for an email the customer already gave or
  // creating a second account for the same person. Resolves to the user, or
  // null when the server really has no session.
  async function refreshMe() {
    if (signedIn) return me;
    try {
      const r = await fetch('/api/auth/me', { cache: 'no-store', credentials: 'same-origin' });
      if (!r.ok) return null;
      const d = await r.json();
      const user = d && d.currentUser;
      if (user && user.id) {
        if (onMe) onMe(user);
        setIdentity(null);
        return user;
      }
    } catch (e) { /* treated as not signed in; ensureSession reports the real error */ }
    return null;
  }

  async function checkEmail(value) {
    const v = String(value || '').trim().toLowerCase();
    setLoginError(null);
    setLinkSent(false);
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v) || signedIn) return setIdentity(null);
    if (await refreshMe()) return;
    postJson('/api/checkout/identify', { email: v })
      .then((d) => {
        setIdentity(d);
        // The account wall itself left no telemetry (#308).
        if (d && d.known) track('checkout_account_wall', { can_login: !!d.can_login, at: 'email_blur' });
      })
      .catch(() => setIdentity(null));
  }

  async function logIn(e) {
    e.preventDefault();
    setLoginBusy(true);
    setLoginError(null);
    try {
      const turnstileToken = turnstile.token;
      const recaptcha = turnstileToken ? null : await recaptchaToken('login');
      const d = await postJson('/api/auth/login', {
        username: email.trim().toLowerCase(), password, turnstileToken, recaptcha,
        returnTo: window.location.pathname + window.location.search,
      });
      setIdentity(null);
      setPassword('');
      if (onMe) onMe(d.currentUser);
    } catch (err) {
      setLoginError(err.message);
      track('checkout_inline_login_failed', { code: err.code || null, status: err.status || null });
    } finally {
      setLoginBusy(false);
    }
  }

  async function sendSignInLink() {
    setLoginError(null);
    try {
      // The link is usually opened in another browser (email app → system
      // browser); the way back to this order travels on the token (#308).
      await postJson('/api/checkout/resume', {
        email: email.trim().toLowerCase(),
        returnTo: window.location.pathname + window.location.search,
      });
      setLinkSent(true);
      track('checkout_signin_link_sent', { can_login: !!(identity && identity.can_login) });
    } catch (err) {
      setLoginError(err.message);
    }
  }

  // `live` is the server's answer from refreshMe() when this render's `me`
  // may be stale (state updates land after the current tick).
  function validate(live) {
    const signed = signedIn || !!live;
    if (!signed && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return 'Enter the email for your receipt.';
    if (!signed && identity && identity.known) return wallMessage(identity.can_login);
    if (!agree) return 'Please accept the terms to continue.';
    if (method === 'balance' && !signed) return 'Sign in to pay from your balance.';
    if (method === 'balance' && balanceShort) return `Your balance is too low for this ${isEsim ? 'plan' : 'topup'}.`;
    if (method === 'balance_usd' && !signed) return 'Sign in to pay from your USD wallet.';
    if (method === 'balance_usd' && usdBalance !== null && selected && selected.total_usd && usdBalance < selected.total_usd) return 'Your USD balance is too low for this plan.';
    if ((method === 'omise' || method === 'omise-tt') && !savedCardId) {
      if (!card.first.trim() || !card.last.trim()) return 'Enter the name on the card.';
      if (!card.number.trim() || !card.exp.trim() || !card.cvv.trim()) return 'Enter the card number, expiry and CVV.';
      const exp = parseExpiry(card.exp);
      if (!exp) return 'Expiry should look like MM/YY.';
      {
        const month = parseInt(exp.month, 10);
        const year = parseInt(exp.year, 10);
        if (month < 1 || month > 12) return 'Invalid expiry month.';
        const now = new Date();
        if (year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1)) return 'This card has expired.';
      }
      if (ADDRESS_COUNTRIES.includes(card.country)
        && (!card.street.trim() || !card.city.trim() || !card.postal.trim() || (card.country === 'US' && !card.state))) {
        return 'Enter the billing address for this card.';
      }
    }
    return null;
  }

  // Guest checkout's one server-side prerequisite: a session the payment
  // routes will accept. `live` is refreshMe()'s answer when the caller already
  // asked (false = checked, no session); undefined means check here first.
  async function ensureSession(live) {
    if (signedIn || live) return;
    if (live === undefined && (await refreshMe())) return;
    try {
      const d = await postJson('/api/checkout/guest', {
        email: email.trim().toLowerCase(),
        firstName: card.first.trim(),
        lastName: card.last.trim(),
        turnstileToken: turnstile.token,
      });
      if (onMe && d.currentUser) onMe(d.currentUser);
    } catch (err) {
      if (err.code === 'account_exists') {
        // A password-less guest (often this page's own account, #305) gets the
        // sign-in link, not a password box they could never fill in (#237).
        const who = await postJson('/api/checkout/identify', { email: email.trim().toLowerCase() })
          .catch(() => ({ known: true, can_login: true }));
        setIdentity({ known: true, can_login: !!who.can_login });
        track('checkout_account_wall', { can_login: !!who.can_login, at: 'place_order' });
        focusKnownBox(); // the box renders on the next commit; the effect finishes the job
        throw new Error(wallMessage(!!who.can_login));
      }
      throw err;
    }
  }

  function orderPayload() {
    const isIntl = params.kind === 'intl';
    const isWondd = params.kind === 'wondd';
    const isGift = params.kind === 'gift';

    // For gift cards, inject recipientName into the dingSku blob so the
    // server can extract it without modifying every payment route.
    let dingSku = quote.dingSku;
    if (isGift && dingSku && params.phone) {
      try {
        const blob = JSON.parse(atob(decodeURIComponent(dingSku)));
        blob.recipientName = params.phone;
        dingSku = encodeURIComponent(btoa(JSON.stringify(blob)));
      } catch (e) { /* use original dingSku */ }
    }

    return {
      phoneNumber: isGift ? '' : params.phone,
      amount: quote.topup_thb,
      carrier: isWondd ? (quote.product && quote.product.name) : isIntl ? (quote.product && quote.product.name) : isGift ? (quote.product && quote.product.name) : params.providerName,
      CountryIso: params.iso || 'TH',
      CountryName: countryName(params.iso || 'TH'),
      ProviderCode: isWondd ? `WONDD-${params.carrier}` : isIntl ? `RL-${params.operator}` : isGift ? `ZENDIT-${quote.product && quote.product.name}` : params.provider,
      dingSku,
      bfp: bfp(),
    };
  }

  function cardFields() {
    const exp = parseExpiry(card.exp);
    const data = {
      name: `${card.first.trim()} ${card.last.trim()}`.trim(),
      number: card.number.replace(/\s+/g, ''),
      expiration_month: exp.month,
      expiration_year: exp.year,
      security_code: card.cvv.trim(),
      country: card.country,
    };
    if (ADDRESS_COUNTRIES.includes(card.country)) {
      data.street1 = card.street.trim();
      data.city = card.city.trim();
      data.state = card.state;
      data.postal_code = card.postal.trim();
    }
    return data;
  }

  // eSIM plans have their own order model and rails (card or balance); the
  // identity, card form and totals above are shared with topups.
  async function placeEsimOrder() {
    const body = { slug: params.code, days: quote.plan.is_daily ? quote.plan.days : undefined, bfp: bfp() };

    if (method === 'omise') {
      let esimCardPayload;
      if (useSavedCard) {
        esimCardPayload = { cardId: useSavedCard };
      } else {
        const token = await omiseToken(cardFields()).catch((tokenErr) => {
          tokenErr._isTokenError = true;
          throw tokenErr;
        });
        esimCardPayload = { cardToken: token.id, cardId: token.card.id };
      }
      const reply = await postJson('/api/payment/esim/card', Object.assign(body, esimCardPayload));
      if (reply && reply.authorize_uri) {
        window.location = reply.authorize_uri;
        return;
      }
      throw new Error('The card page could not be opened. Try again or pay from your balance.');
    }

    // Stripe Checkout: the customer pays on Stripe's page in the currency they
    // were quoted, and the webhook provisions the eSIM. No card fields here.
    if (method === 'stripe-esim') {
      const reply = await postJson('/api/payment/esim/stripe', Object.assign(body, {
        currency: (selected && selected.settles_in) || (cur && cur.currency),
      }));
      if (reply && reply.checkout_url) {
        window.location = reply.checkout_url;
        return;
      }
      throw new Error('The payment page could not be opened. Try again or pay from your balance.');
    }

    // Stripe Crypto (USDC) — always USD, redirects to Stripe's crypto page.
    if (method === 'stripe-crypto') {
      const reply = await postJson('/api/payment/esim/crypto', body);
      if (reply && reply.checkout_url) {
        window.location = reply.checkout_url;
        return;
      }
      throw new Error('The payment page could not be opened. Try again or pay with a card.');
    }

    // Payment Links+ (Thai Wallets, Thai bank, Alipay) — redirects to Omise checkout.
    if (method === 'thai-wallets' || method === 'thai-bank' || method === 'alipay') {
      const reply = await postJson(`/api/payment/esim/${method}`, body);
      if (reply && reply.payment_url) {
        window.location = reply.payment_url;
        return;
      }
      throw new Error('The payment page could not be opened. Try again or pay with a card.');
    }

    // Xendit (PHP) — redirects to Xendit's invoice page.
    if (method === 'xendit') {
      const reply = await postJson('/api/payment/esim/xendit', body);
      if (reply && reply.payment_url) {
        window.location = reply.payment_url;
        return;
      }
      throw new Error('The payment page could not be opened. Try again or pay with a card.');
    }

    if (method === 'balance_usd') {
      const reply = await postJson('/api/payment/esim/balance-usd', body);
      setEsimOrderId(reply.order.id);
      setEsimStage('provisioning');
      return;
    }

    const reply = await postJson('/api/payment/esim/balance', body);
    setEsimOrderId(reply.order.id);
    setEsimStage('provisioning');
  }

  async function placeOrder(e) {
    e.preventDefault();
    // Don't trust a signed-out page until the server confirms it (#305).
    let live = signedIn;
    if (!live) {
      setPlacing(true);
      live = !!(await refreshMe());
      setPlacing(false);
    }
    const problem = validate(live);
    if (problem) {
      setPayError(problem);
      setPayErrorMore(null);
      // Blocked before any request: without this the attempt is invisible (#261).
      track('checkout_validation_error', { reason: problem, kind: params.kind || null, method: method || null, signed_in: !!live });
      // The email field sits at the top of the form; on phones the notice
      // beside Place order is far from it, so take the user to the field.
      if (!live && problem.startsWith('That email already')) {
        focusKnownBox();
      } else if (!live && emailRef.current && problem.startsWith('Enter the email')) {
        emailRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        emailRef.current.focus({ preventScroll: true });
      }
      return;
    }

    setPayError(null);
    setPayErrorMore(null);
    setCardLinkUrl(null);
    setPlacing(true);
    try {
      await ensureSession(live);

      if (quote.esim) {
        await placeEsimOrder();
        return;
      }

      const order = orderPayload();

      if (method === 'omise' || method === 'omise-tt') {
        const isTT = method === 'omise-tt';
        let omisePayload;

        if (savedCardId) {
          // Charge a previously saved card — no tokenization needed
          const turnstileToken = turnstile.token;
          const recaptcha = turnstileToken ? null : await recaptchaToken('newusercheckout');
          omisePayload = { cardId: savedCardId, is3ds: !isTT, turnstileToken, recaptcha };
        } else {
          // New card — tokenize via Omise Vault (TT uses its own public key)
          const tokenize = isTT ? omiseTokenTT : omiseToken;
          const token = await tokenize(cardFields()).catch((tokenErr) => {
            tokenErr._isTokenError = true;
            throw tokenErr;
          });
          const turnstileToken = turnstile.token;
          const recaptcha = turnstileToken ? null : await recaptchaToken('newusercheckout');
          omisePayload = { cardToken: token.id, cardId: token.card.id, is3ds: !isTT, turnstileToken, recaptcha };
        }

        const endpoint = isTT ? '/api/payment/omise/tt' : '/api/payment/omise';
        const reply = await postJson(endpoint, Object.assign(order, omisePayload)).catch((err) => {
          // The card route answers a bare 400 for its bot/limit checks.
          if (err.status === 400 && /^HTTP 400$/.test(err.message)) {
            throw new Error("We couldn't verify this checkout. Reload the page and try again, or pay with USDC or your balance.");
          }
          throw err;
        });
        if (reply.order && reply.order.status === 'awaiting_authorization' && reply.order.charge) {
          window.location = reply.order.charge.authorize_uri;
          return;
        }
        setTopupOrderId(reply.order.id);
        setTopupStage('payment');
      } else if (method === 'stripe-crypto') {
        const reply = await postJson('/api/payment/stripe-checkout/topup-crypto', order);
        if (reply && reply.url) {
          window.location = reply.url;
          return;
        }
        throw new Error('Stripe did not return a payment page.');
      } else if (method === 'thai-wallets') {
        order.total = selected.total_thb;
        const reply = await postJson('/api/payment/thai-wallets', order);
        if (reply && reply.payment_url) {
          window.location = reply.payment_url;
          return;
        }
        throw new Error('The payment page could not be opened. Try again or pay with a card.');
      } else if (method === 'thai-bank' || method === 'alipay') {
        order.total = selected.total_thb;
        const reply = await postJson(method === 'alipay' ? '/api/payment/alipay' : '/api/payment/thai-bank/order', order);
        const url = reply && (reply.payment_url || reply.href);
        if (url) {
          window.location = url;
          return;
        }
        throw new Error('The payment page could not be opened. Try again or pay with a card.');
      } else if (method === 'xendit') {
        order.total = selected.settlement_total;
        const reply = await postJson('/api/payment/xendit', order);
        if (reply && reply.payment_url) {
          window.location = reply.payment_url;
          return;
        }
        throw new Error('Xendit did not return a payment page.');
      } else if (method === 'balance') {
        const reply = await postJson('/api/payment/balance', Object.assign(order, { total: selected.total_thb }));
        setTopupOrderId(reply.order.id);
        setTopupStage('payment');
      }
    } catch (err) {
      // A refused order leaves no order row and no log line; this is the only
      // record that the customer hit a wall (#285).
      if (posthog.__loaded) {
        posthog.capture('checkout_payment_error', {
          method,
          kind: params.kind,
          iso: params.iso || null,
          status: err.status || null,
          code: err.code || null,
          message: err.message || null,
        });
      }
      setPayErrorMore(err.learnMore || null);
      if (LIMIT_CODES.has(err.code)) {
        // Purchasing cap (#278): the server message already names the limit,
        // what is used and when the amount fits again. A hosted card page
        // would be rejected by the same rule, so don't offer it.
        setPayError(limitErrorText(err));
      } else if ((method === 'omise' || method === 'omise-tt') && !quote.esim) {
        // Card failed (tokenization, 3DS, fraud check, bank decline, etc.) — offer hosted checkout fallback
        setPayError((err.message || 'Payment failed.') + ' You can try paying on a secure Omise-hosted page instead.');
        setCardLinkUrl('pending'); // signals the button should show
      } else {
        setPayError(err.message || 'Payment failed.');
      }
    } finally {
      setPlacing(false);
    }
  }

  // Generate a hosted card checkout link (Payment Links+ fallback)
  async function requestCardLink() {
    setCardLinkBusy(true);
    try {
      await ensureSession();
      const order = orderPayload();
      const reply = await postJson('/api/payment/card-link/topup', order);
      if (reply && reply.payment_url) {
        setCardLinkUrl(reply.payment_url);
        window.open(reply.payment_url, '_blank', 'noopener');
      } else {
        setPayError('Could not create a payment link. Please try again or use a different payment method.');
        setCardLinkUrl(null);
      }
    } catch (err) {
      setPayError(err.message || 'Could not create payment link.');
      setCardLinkUrl(null);
    } finally {
      setCardLinkBusy(false);
    }
  }

  // ---- eSIM: paid, now being delivered ----
  if (esimStage) {
    const installUrl = (esimOrder && esimOrder.install_url) || `/esim/${esimOrderId}`;
    // Same plan again, with the method grid open: the reuse rule on the server
    // hands back the still-open Stripe page when there is one (#299)
    const retryHref = esimOrder && esimOrder.slug
      ? `/checkout?type=esim&code=${encodeURIComponent(esimOrder.slug)}${esimOrder.days ? `&days=${encodeURIComponent(esimOrder.days)}` : ''}`
      : '/';
    const unpaidFailure = esimOrder && esimOrder.payment_failure;
    const unpaidDeclined = !!(unpaidFailure && unpaidFailure.code && unpaidFailure.code !== 'session_expired');
    const unpaidReason = unpaidDeclined && unpaidFailure.message
      ? String(unpaidFailure.message).trim().replace(/\.$/, '')
      : '';
    // "received safely" is only true once the order itself says paid (#317)
    const paidForSure = !!(esimOrder && esimOrder.status === 'paid');
    const clockIcon = (
      <svg className="delayed-icon" viewBox="0 0 48 48" width="48" height="48" aria-hidden="true">
        <circle cx="24" cy="24" r="22" fill="none" stroke="var(--accent, #0a8a6e)" strokeWidth="2.5" opacity=".2" />
        <circle cx="24" cy="24" r="22" fill="none" stroke="var(--accent, #0a8a6e)" strokeWidth="2.5"
          strokeDasharray="138" strokeDashoffset="46" strokeLinecap="round" />
        <path d="M24 14v11l7 4" fill="none" stroke="var(--accent, #0a8a6e)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
    return (
      <div className="page">
        <Header me={me} active="esim" currency={currency} onCurrency={onCurrency} />
        <div className="checkout-wrap">
          <div className="cocard">
            {(esimStage === 'verifying' || esimStage === 'provisioning') && (
              <div className="provisioning">
                <div className="spinner" aria-hidden="true" />
                <b>{esimStage === 'verifying' ? 'Confirming your payment…' : 'Preparing your eSIM…'}</b>
                <p>This usually takes under a minute. Keep this window open — we'll also email your install details.</p>
              </div>
            )}
            {esimStage === 'ready' && (
              <div className="provisioning done">
                <div className="bigcheck" aria-hidden="true">✓</div>
                <b>Your eSIM is ready</b>
                <p>Install details are also on their way to {email || (me && me.email) || 'your email'}.</p>
                <a className="btn" style={{ padding: '10px 26px' }} href={installUrl} target="_blank" rel="noopener">
                  Install your eSIM
                </a>
                <p style={{ marginTop: 16, fontSize: 13 }}><a href="/orders" style={{ color: '#888' }}>View order history</a></p>
                <ReferralPromoCard me={me} source="esim_success" />
              </div>
            )}
            {esimStage === 'delayed' && (
              <div className="provisioning">
                {clockIcon}
                <b>Taking longer than usual</b>
                <p style={{ maxWidth: 340 }}>
                  {paidForSure
                    ? "Your payment was received safely. We're preparing your eSIM — this sometimes takes a few extra minutes. "
                    : "We're still checking on your order — this sometimes takes a few extra minutes. "}
                  You can safely close this page; we'll email your install details as soon as it's ready.
                </p>
                <button className="btn" style={{ padding: '10px 26px' }} onClick={checkEsimStatus} disabled={checking}>
                  {checking ? 'Checking…' : 'Check status'}
                </button>
                {showSupport && (
                  <p style={{ marginTop: 14, fontSize: 13, color: '#888' }}>
                    Still waiting? <a href="/support" style={{ color: '#888' }}>Contact support</a>
                  </p>
                )}
              </div>
            )}
            {esimStage === 'awaiting' && (
              <div className="provisioning">
                {clockIcon}
                <b>We haven't received your payment yet</b>
                <p style={{ maxWidth: 340 }}>
                  If you just paid, it can take a minute to confirm — check again in a moment.
                  If you left the payment page, nothing was charged and you can try again.
                </p>
                <button className="btn" style={{ padding: '10px 26px' }} onClick={checkEsimStatus} disabled={checking}>
                  {checking ? 'Checking…' : 'Check status'}
                </button>
                <p style={{ marginTop: 16, fontSize: 13 }}>
                  <a href={retryHref} style={{ color: '#888' }}>Try again</a>
                  {' · '}
                  <a href="/support" style={{ color: '#888' }}>Contact support</a>
                </p>
              </div>
            )}
            {esimStage === 'unpaid' && (
              <div className="provisioning">
                <div className="bigcheck fail" aria-hidden="true">✕</div>
                <b>{unpaidDeclined ? 'Payment not accepted' : 'Payment not completed'}</b>
                <p style={{ maxWidth: 340 }}>
                  {unpaidDeclined
                    ? (unpaidReason
                      ? `Your payment was not accepted: ${unpaidReason.charAt(0).toLowerCase()}${unpaidReason.slice(1)}. No money was taken.`
                      : 'Your payment was not accepted. No money was taken.')
                    : 'The payment page was closed before the payment went through. No money was taken.'}
                </p>
                <a className="btn" style={{ padding: '10px 26px' }} href={retryHref}>
                  {unpaidDeclined ? 'Try another card' : 'Try again'}
                </a>
                <p style={{ marginTop: 16, fontSize: 13 }}>
                  <a href={retryHref} style={{ color: '#888' }}>Pay another way</a>
                  {' · '}
                  <a href="/support" style={{ color: '#888' }}>Contact support</a>
                </p>
              </div>
            )}
            {esimStage === 'failed' && (
              <div className="provisioning">
                <div className="bigcheck fail" aria-hidden="true">✕</div>
                <b>We couldn't prepare this eSIM</b>
                <p>
                  A refund will be processed automatically. Try another plan,
                  or contact support if this keeps happening.
                </p>
                <a className="btn" style={{ padding: '10px 26px' }} href="/">Back to plans</a>
              </div>
            )}
          </div>
          {payError && (
            <div className="notice payerr">
              {payError}
              {payErrorMore && <> <a href={payErrorMore} target="_blank" rel="noopener">Learn more</a></>}
            </div>
          )}
        </div>
        <SiteFooterV2 currency={currency} onCurrency={onCurrency} />
      </div>
    );
  }

  // ---- Deposit return: show the verified status, not the URL's claim (#281) ----
  if (isDepositReturn) {
    const shortId = (depositInfo && depositInfo.short_id)
      || ('ORD-' + params.returnId.replace(/-/g, '').slice(0, 8).toUpperCase());
    const credited = depositStage === 'credited';
    const depositFailed = depositStage === 'failed';
    const depositPending = depositStage === 'pending';
    const headline = credited ? 'Balance deposited'
      : depositFailed ? 'Deposit not completed'
      : depositPending ? 'Deposit still processing'
      : 'Confirming your deposit\u2026';
    const sub = credited ? `Your deposit ${shortId} has been credited to your balance.`
      : depositFailed ? `Deposit ${shortId} was declined or expired. Nothing was added to your balance.`
      : depositPending ? `We're still confirming deposit ${shortId}. You can close this page \u2014 your balance updates as soon as the payment settles.`
      : `Checking deposit ${shortId} with the bank.`;
    return (
      <div className="page">
        <Header me={me} active="recharge" currency={currency} onCurrency={onCurrency} />
        <div className="checkout-wrap">
          <div className="cocard tracker-card">
            <div className="tracker-ring-wrap">
              <svg className="tracker-ring" viewBox="0 0 120 120" aria-hidden="true">
                <circle className="ring-bg" cx="60" cy="60" r="52" />
                <circle
                  className={`ring-fill ${credited ? 'complete' : ''} ${depositFailed ? 'fail' : ''} ${depositPending ? 'delayed' : ''}`}
                  cx="60" cy="60" r="52"
                  style={{ strokeDashoffset: credited ? 0 : depositFailed ? 326 : (326 - (326 * 0.75)) }}
                />
              </svg>
              <div className="tracker-ring-inner">
                {credited && (
                  <svg className="drawn-check" viewBox="0 0 52 52" aria-hidden="true">
                    <path className="check-path" d="M14 27l8 8 16-16" />
                  </svg>
                )}
                {depositFailed && <span className="ring-icon fail">!</span>}
                {depositPending && <span className="ring-icon delayed">{'\u2026'}</span>}
                {!credited && !depositFailed && !depositPending && <div className="ring-pulse" />}
              </div>
            </div>
            <div className="tracker-headline">{headline}</div>
            <div className="tracker-sub">{sub}</div>
            {credited && (
              <a className="btn" style={{ padding: '10px 26px', marginTop: 18 }} href="/recharge">
                Continue shopping
              </a>
            )}
            {depositFailed && (
              <a className="btn" style={{ padding: '10px 26px', marginTop: 18 }} href="/wallet">
                Try again
              </a>
            )}
            {depositPending && (
              <a className="btn" style={{ padding: '10px 26px', marginTop: 18 }} href="/wallet">
                View balance
              </a>
            )}
            <div style={{ marginTop: 10 }}><a href="/orders" style={{ color: 'var(--muted)', fontSize: 12 }}>View order history</a></div>
          </div>
        </div>
        <SiteFooterV2 currency={currency} onCurrency={onCurrency} />
      </div>
    );
  }

  // ---- Topup: animated order tracker ----
  if (topupStage) {
    const STAGES = [
      { key: 'payment', label: 'Payment confirmed', icon: 'card', tip: 'Your payment has been received' },
      { key: 'sending', label: 'Requesting topup', icon: 'send', tip: 'Request submitted to carrier' },
      { key: 'processing', label: 'Processing', icon: 'gear', tip: 'Carrier is delivering your credit' },
      { key: 'success', label: 'Complete', icon: 'check', tip: 'Credit delivered successfully' },
    ];
    const ICON = {
      card: <svg className="tstep-icon" viewBox="0 0 16 16"><rect x="1.5" y="3" width="13" height="10" rx="1.5" /><path d="M1.5 6.5h13" /></svg>,
      send: <svg className="tstep-icon" viewBox="0 0 16 16"><path d="M2 8h9m0 0L7.5 4.5M11 8l-3.5 3.5" /><path d="M14 4v8" opacity=".4" /></svg>,
      gear: <svg className="tstep-icon" viewBox="0 0 16 16"><circle cx="8" cy="8" r="2.2" /><path d="M8 1.5v2m0 9v2m-4.6-11L5 5m6 6 1.6 1.6M1.5 8h2m9 0h2M3.4 12.6 5 11m6-6 1.6-1.6" /></svg>,
      check: <svg className="tstep-icon" viewBox="0 0 16 16"><path d="M3.5 8.5l3 3 6-6" /></svg>,
    };
    const stageIdx = STAGES.findIndex((s) => s.key === topupStage);
    const isFailed = topupStage === 'failed';
    const isDeclined = isFailed && !!payFailure; // payment never went through (#290)
    const isComplete = topupStage === 'success';
    const isDelayed = topupStage === 'delayed';
    const declineText = (f) => {
      if (f.code === 'expired') return 'the payment page expired before the payment was completed';
      const m = String(f.message || '').trim().replace(/\.$/, '');
      return m ? m.charAt(0).toLowerCase() + m.slice(1) : '';
    };

    const ringOffset = isFailed ? 326
      : isDelayed ? (326 - (326 * 0.75))
      : (326 - (326 * Math.min((stageIdx + 1) / STAGES.length, 1)));

    // An expired Xendit invoice is not a decline: the page only takes
    // Philippine wallets and banks, which the customer may not have (#279)
    const xenditExpired = isDeclined && failedMethod === 'xendit' && payFailure.code === 'expired';
    const subText = xenditExpired
      ? 'The Maya / ShopeePay / GrabPay payment page expired before it was paid. It only takes Philippine wallets and bank accounts. No money was taken.'
      : isDeclined
      ? (declineText(payFailure)
        ? `Your payment was not accepted: ${declineText(payFailure)}. No money was taken.`
        : 'Your payment was not accepted. No money was taken.')
      : isFailed
      ? "Your topup couldn't be completed. A refund will be processed automatically."
      : isComplete
        ? (quote && quote.product && quote.product.validity_months
          ? `+${quote.product.validity_months} month${quote.product.validity_months > 1 ? 's' : ''} validity added to ${params.phone}`
          : `${quote && quote.product ? `${quote.product.receive_iso === 'THB' ? '฿' : '$'}${Number(quote.product.receive_value)}` : 'Credit'} sent to ${params.phone || (done && done.phone) || 'your number'}`)
      : isDelayed
        ? null
        : null;

    return (
      <div className="page">
        <Header me={me} active="recharge" currency={currency} onCurrency={onCurrency} />
        <div className="checkout-wrap">
          <div className="cocard tracker-card">
            {/* Animated progress ring */}
            <div className="tracker-ring-wrap">
              <svg className="tracker-ring" viewBox="0 0 120 120" aria-hidden="true">
                <circle className="ring-bg" cx="60" cy="60" r="52" />
                <circle
                  className={`ring-fill ${isComplete ? 'complete' : ''} ${isFailed ? 'fail' : ''} ${isDelayed ? 'delayed' : ''}`}
                  cx="60" cy="60" r="52"
                  style={{ strokeDashoffset: ringOffset }}
                />
              </svg>
              <div className="tracker-ring-inner">
                {isComplete && (
                  <svg className="drawn-check" viewBox="0 0 52 52" aria-hidden="true">
                    <path className="check-path" d="M14 27l8 8 16-16" />
                  </svg>
                )}
                {isFailed && <span className="ring-icon fail">!</span>}
                {isDelayed && <span className="ring-icon delayed">{'\u2026'}</span>}
                {!isComplete && !isFailed && !isDelayed && <div className="ring-pulse" />}
              </div>
            </div>

            {/* Status headline */}
            <div className="tracker-headline">
              {isDeclined ? 'Payment not accepted'
                : isFailed ? 'Topup could not be delivered'
                : isComplete ? 'Topup delivered'
                : isDelayed ? 'Taking longer than usual'
                : 'Sending your topup...'}
            </div>

            {/* Step timeline */}
            <div className="tracker-steps">
              {STAGES.map((stage, i) => {
                const reached = !isFailed && !isDelayed && stageIdx >= i;
                const active = !isFailed && !isDelayed && stageIdx === i && !isComplete;
                const delayedReached = isDelayed && i < 3;
                const delayedActive = isDelayed && i === 3;
                const show = reached || delayedReached;
                const pulse = active || delayedActive;
                const lineFilled = (!isFailed && !isDelayed && stageIdx > i) || (isDelayed && i < 2);
                return (
                  <div key={stage.key} className={`tstep ${(show || pulse) ? 'reached' : ''} ${pulse ? 'active' : ''}`}>
                    <div className="tstep-dot">
                      {show && !pulse ? (
                        <svg className="tstep-check" viewBox="0 0 16 16"><path d="M3.5 8.5l3 3 6-6" /></svg>
                      ) : pulse ? (
                        <div className="tstep-pulse" />
                      ) : (
                        <div className="tstep-empty" />
                      )}
                    </div>
                    {i < STAGES.length - 1 && <div className={`tstep-line ${lineFilled ? 'filled' : ''}`} />}
                    <div className="tstep-text">
                      <span className="tstep-label" title={stage.tip}>{ICON[stage.icon]}{stage.label}</span>
                    </div>
                  </div>
                );
              })}
              {isFailed && (
                <div className="tstep reached fail-step">
                  <div className="tstep-dot"><span className="tstep-x">!</span></div>
                  <div className="tstep-text">
                    <span className="tstep-label">{isDeclined ? 'Declined' : 'Failed'}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Sub text (below timeline) */}
            {subText && <div className="tracker-sub">{subText}</div>}
            {isDelayed && (
              <div className="tracker-sub">
                <svg className="sub-icon" viewBox="0 0 16 16"><rect x="1.5" y="3.5" width="13" height="9" rx="1.5" /><path d="M1.5 4l6.5 5 6.5-5" /></svg>
                {" Your topup is still being processed. You can safely close this page \u2014 we'll email your receipt when it's done."}
              </div>
            )}

            {/* Actions */}
            {isComplete && topupOrderId && (
              <>
                <a className="btn" style={{ padding: '10px 26px', marginTop: 18 }} href={`/receipt/${topupOrderId}`}>
                  <svg className="btn-icon" viewBox="0 0 16 16"><path d="M4 1.5h8a1 1 0 0 1 1 1v12l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5-2 1.5v-12a1 1 0 0 1 1-1z" /><path d="M5.5 5h5M5.5 7.5h5M5.5 10h3" /></svg>
                  View receipt
                </a>
                <div style={{ marginTop: 10 }}><a href="/orders" style={{ color: 'var(--muted)', fontSize: 12 }}>View order history</a></div>
                <ReferralPromoCard me={me} source="topup_success" />
              </>
            )}
            {isFailed && (
              // Money never taken: pay the same product again, card first,
              // instead of rebuilding the order from /recharge (#279)
              <a className="btn" style={{ padding: '10px 26px', marginTop: 18 }} href={isDeclined && retryUrl ? retryUrl : '/recharge'}>
                <svg className="btn-icon" viewBox="0 0 16 16"><path d="M2.5 6.5a6 6 0 0 1 10.5-1M13.5 9.5a6 6 0 0 1-10.5 1" /><path d="M2.5 3v3.5H6M13.5 13V9.5H10" /></svg>
                {xenditExpired && retryUrl ? 'Pay with card instead' : isDeclined ? 'Try another card or payment method' : 'Try again'}
              </a>
            )}
            {isDelayed && (
              <a className="btn" style={{ padding: '10px 26px', marginTop: 18 }} href="/orders">
                <svg className="btn-icon" viewBox="0 0 16 16"><circle cx="8" cy="8" r="6" /><path d="M8 4.5V8l2.5 2.5" /></svg>
                Check order history
              </a>
            )}
          </div>
        </div>
        <SiteFooterV2 currency={currency} onCurrency={onCurrency} />
      </div>
    );
  }

  // Back to where the customer picked the product, not the Thai /recharge flow.
  const startAgainHref = isEsim ? '/'
    : params.kind === 'intl' ? (SLUG_BY_ISO[params.iso] ? `/topup/${SLUG_BY_ISO[params.iso]}` : '/recharge')
    : params.kind === 'gift' ? '/gifts'
    : params.kind === 'wondd' && params.carrier ? `/topup/thailand/${encodeURIComponent(params.carrier)}`
    : '/recharge';

  // No price means nothing can be paid: show only the error, not the card form
  // and a silently disabled Place order button.
  if (quoteError && !quote) {
    return (
      <div className="page">
        <Header me={me} active={isEsim ? 'esim' : 'recharge'} currency={currency} onCurrency={onCurrency} />
        <div className="checkout-wrap">
          <h1 className="cotitle">Checkout</h1>
          <div className="cocard">
            <div className="notice">{quoteError} <a href={startAgainHref}>Choose another product</a></div>
          </div>
        </div>
        <SiteFooterV2 currency={currency} onCurrency={onCurrency} />
      </div>
    );
  }

  return (
    <div className="page">
      <Header me={me} active={isEsim ? 'esim' : 'recharge'} currency={currency} onCurrency={onCurrency} />

      <div className="checkout-wrap">
        <h1 className="cotitle">Checkout</h1>

        {quoteError && (
          <div className="cocard">
            <div className="notice">{quoteError} <a href={startAgainHref}>Start again</a></div>
          </div>
        )}

        {/* No supplier float right now (#382): the customer decides before paying.
            Paying places the order for manual review; it is sent once float is back. */}
        {quote && quote.auto_fulfillment === false && (
          <div className="cocard">
            <div className="notice">
              {quote.fulfillment_message}
              {' '}<a href={startAgainHref}>Choose another product</a>
            </div>
          </div>
        )}

        {/* ---------- order summary ---------- */}
        <div className="cocard">
          <h3 className="coh">Order summary</h3>
          {isEsim ? (
            quote && (
              <>
                <div className="sumrow"><span>Plan</span><b>{quote.plan.name}</b></div>
                <div className="sumrow"><span>Data</span><b>{quote.plan.data_label || '—'}{quote.plan.is_daily ? '/day' : ''}</b></div>
                <div className="sumrow">
                  <span>Validity</span>
                  <b>
                    {quote.plan.is_daily
                      ? `${quote.plan.days} day${quote.plan.days > 1 ? 's' : ''}`
                      : (quote.plan.duration ? `${quote.plan.duration} ${quote.plan.duration_unit || 'day'}${quote.plan.duration > 1 ? 's' : ''}` : '—')}
                  </b>
                </div>
              </>
            )
          ) : (
            <>
              <div className="sumrow"><span>{params.isGift ? 'Account' : 'Phone'}</span><b className="mono">{params.phone || '—'}</b></div>
              <div className="sumrow">
                <span>{params.isGift ? 'Product' : 'Carrier'}</span>
                <b>
                  {quote && quote.product && quote.product.logo_url && (
                    <img className="carrier-logo sm" src={quote.product.logo_url} alt="" loading="lazy" />
                  )}
                  {params.providerName || (quote && quote.product && quote.product.name) || ''}{params.iso ? ` · ${countryName(params.iso)}` : ''}
                </b>
              </div>
            </>
          )}
          {quote && (
            <>
              {!isEsim && quote.product.validity_months && (
                <div className="sumrow">
                  <span>Validity extension</span>
                  <b>+{quote.product.validity_months} month{quote.product.validity_months > 1 ? 's' : ''}</b>
                </div>
              )}
              {!isEsim && (
                <div className="sumrow">
                  <span>{quote.product.validity_months ? 'Cost' : 'They receive'}</span>
                  <b>
                    {quote.product.receive_value != null
                      ? `${quote.product.receive_value} ${quote.product.receive_iso || ''}`.trim()
                      : `$${quote.product.send_value}`}
                  </b>
                </div>
              )}
              <div className="sumrow">
                <span>{isEsim ? 'Plan price' : 'Topup amount'}</span>
                <b className="mono">{money(quote.topup_thb, isEsim ? cur && cur.base : cur && cur.topup)}</b>
              </div>
              <div className="sumrow">
                <span>Service fee{quote.fees_waived ? ' (waived)' : ''}</span>
                <b className="mono">{selected ? money(selected.fee_thb, selected.display && selected.display.fee) : '—'}</b>
              </div>
              {selected && selected.fee_is_minimum && (
                <div className="sumnote">
                  Card processing costs the same whatever the plan costs, so small
                  plans carry a minimum fee. Paying from your balance avoids it.
                </div>
              )}
              <div className="sumrow total">
                <span>Total</span>
                <b className="mono">{selected ? money(selected.total_thb, selected.display && selected.display.total) : '—'}</b>
              </div>
              <div className="corate">
                {(() => {
                  const settles = (selected && selected.settles_in) || 'THB';
                  const shown = cur ? cur.currency : 'THB';
                  if (settles === shown) {
                    return `Charged in ${settles}${settles === 'THB' ? ` at ฿${Number(quote.usd_rate).toFixed(2)} per US$1` : ''}.`;
                  }
                  const amount = selected
                    ? fmtMoney(selected.settlement_total, settles, null)
                    : '';
                  return `Shown in ${shown} · charged in ${settles}${amount ? ` — ${amount}` : ''}.`;
                })()}
              </div>
            </>
          )}
          {!quote && !quoteError && <div className="modal-loading">Getting your price…</div>}
        </div>

        <div className="cotrust">
          {TRUST.map((t) => <span key={t}>✓ {t}</span>)}
        </div>

        {/* ---------- contact ---------- */}
        <div className="cocard">
          <h3 className="coh">Contact information</h3>
          {signedIn ? (
            <div className="sumrow">
              <span>Signed in as</span>
              <b>{me.email}</b>
            </div>
          ) : me === undefined ? (
            // Session still being checked (or retried, #305): don't offer a
            // form that may ask for an email the server already knows.
            <input className="coinput" type="email" disabled placeholder="Checking your session…" aria-label="Email address" />
          ) : (
            <>
              <input
                ref={emailRef}
                className="coinput"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="Email address *"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setIdentity(null); }}
                onBlur={(e) => checkEmail(e.target.value)}
                aria-label="Email address"
              />
              <div className="cohint">
                {isEsim
                  ? 'Your receipt and eSIM install details go here. No account needed.'
                  : 'Your receipt and topup confirmation go here. No account needed.'}
              </div>

              {identity && identity.known && (
                <div className="knownbox">
                  <b>Welcome back — that email already has an account.</b>
                  {identity.can_login && (
                    <form className="loginrow" onSubmit={logIn}>
                      <input
                        ref={knownRef}
                        className="coinput"
                        type="password"
                        autoComplete="current-password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        aria-label="Password"
                      />
                      <button type="submit" className="btn small" disabled={loginBusy || !password}>
                        {loginBusy ? 'Signing in…' : 'Sign in'}
                      </button>
                    </form>
                  )}
                  {/* The sign-in link stays on this page; the old "Forgot your
                      password?" link left for /reset-password and the order was
                      lost (#308). The emailed link brings her back here. */}
                  {linkSent ? (
                    <div className="cohint">Check your inbox — the link signs you in and brings you back to this order.</div>
                  ) : identity.can_login ? (
                    <div className="cohint">
                      Forgot your password?{' '}
                      <button type="button" className="linkish" onClick={sendSignInLink}>Email me a sign-in link</button>
                    </div>
                  ) : (
                    <div className="cohint">
                      You checked out as a guest before, so there's no password yet.{' '}
                      <button type="button" className="linkish" ref={knownRef} onClick={sendSignInLink}>Email me a sign-in link</button>
                    </div>
                  )}
                  {loginError && <div className="notice payerr">{loginError}</div>}
                </div>
              )}
            </>
          )}
        </div>

        {/* ---------- payment method ---------- */}
        <div className="cocard">
          <h3 className="coh">Payment method</h3>
          <div className="methods">
            {(quote ? quote.methods : []).filter((m) => {
              if (m.code === 'balance' && (!signedIn || !me.availableBalance || me.availableBalance <= 0)) return false;
              // USD wallet: only show when user has deposited USD
              if (m.code === 'balance_usd' && (!signedIn || !me.hasUsdWallet || usdBalance === null)) return false;
              if (m.regional && !regionalOpen) return false;
              return true;
            }).map((m) => {
              const meta = METHOD_META[m.code] || { label: m.label };
              const usdShort = m.code === 'balance_usd' && usdBalance !== null && m.total_usd && usdBalance < m.total_usd;
              const disabled = (m.code === 'balance' && balanceShort) || usdShort;
              return (
                <button
                  type="button"
                  key={m.code}
                  className={`method ${method === m.code ? 'on' : ''} ${disabled ? 'off' : ''}`}
                  onClick={() => !disabled && setMethod(m.code)}
                  aria-pressed={method === m.code}
                >
                  {/* Orders settle in Baht, but a customer quoted in pesos read
                      the THB badge against the "PHP" tile and picked the page
                      with no card; the total is already shown in pesos (#279).
                      Same for euros and pounds. */}
                  {meta.cur && !(cur && ['PHP', 'EUR', 'GBP'].includes(cur.currency) && meta.cur === 'THB') && <span className="mcur">{meta.cur}</span>}
                  {meta.logos
                    ? <span className="mlogos">{meta.logos.map((l) => <img key={l} src={`${ST}/images/payment-icons/${l}`} alt="" />)}</span>
                    : meta.logo
                      ? <img src={`${ST}/images/payment-icons/${meta.logo}`} alt="" />
                      : <span className="mlabel">{meta.mark}</span>}
                  <span className="mname">{meta.label}</span>
                  {m.discount
                    ? <span className="msub discount">save {money(m.discount.save_thb, m.discount.display && m.discount.display.save)}</span>
                    : m.code === 'balance' && signedIn
                      ? <span className="msub">{balanceShort ? 'Balance too low' : `You have ${fmtBaht(me.availableBalance)}`}</span>
                      : m.code === 'balance_usd' && usdBalance !== null
                        ? <span className="msub">{usdShort ? 'Balance too low' : `You have $${Number(usdBalance).toFixed(2)}`}</span>
                        : meta.sub
                          ? <span className="msub">{meta.sub}</span>
                          : null}
                </button>
              );
            })}
          </div>
          {regionalMethods.length > 0 && !regionalOpen && (
            // Thais abroad do pay via PromptPay, so the rails are a tap away
            // rather than gone — just not the first thing a visitor in Hanoi
            // sees badged as a saving (#291).
            <button type="button" className="methods-more" onClick={() => setShowRegional(true)}>
              Paying from a Thai bank or wallet?
            </button>
          )}
          {xenditRepeat && (
            <p className="methods-min">
              {xenditMethod.recent_expiries} of your recent Maya / ShopeePay / GrabPay payment pages expired unpaid.
              {' '}That page takes Philippine wallets and bank accounts only{method === 'xendit' ? '.' : ' — a card is the quickest way to pay.'}
            </p>
          )}
          {quote && Array.isArray(quote.methods_below_minimum) && quote.methods_below_minimum.length > 0 && (() => {
            // Card, Alipay and USDC each have a gateway floor; a ฿10 Thai
            // top-up offered them all and every one dead-ended (#283). Name
            // the methods and the smallest amount that unlocks them.
            const hidden = quote.methods_below_minimum;
            const labels = Array.from(new Set(hidden.map((m) => (METHOD_META[m.code] || { label: m.label }).label)));
            const minThb = Math.max(...hidden.map((m) => Number(m.minimum_thb) || 0));
            const list = labels.length > 1 ? `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}` : labels[0];
            return (
              <p className="methods-min">
                {list} {labels.length > 1 ? 'need' : 'needs'} an order of at least {fmtBaht(minThb)}.
                {' '}<a href={startAgainHref}>Choose {fmtBaht(minThb)} or more</a> to pay with {labels.length > 1 ? 'them' : 'it'}.
              </p>
            );
          })()}
          {method && method !== 'omise' && method !== 'stripe-esim' && (
            <p className="refund-caveat">If your order is unsuccessful, funds can only be returned to your mobiletopup.com account balance with this payment method.</p>
          )}
        </div>

        {/* ---------- card details ---------- */}
        {(method === 'omise' || method === 'omise-tt') && (
          // ph-no-capture: autocapture must not record saved-card text (name, last 4) or card fields (#351)
          <div className="cocard ph-no-capture">
            <h3 className="coh">Card details</h3>

            {/* Saved cards (only for regular Omise — TT has a separate customer) */}
            {method === 'omise' && savedCards.length > 0 && (
              <div className="saved-cards">
                {savedCards.map((sc) => (
                  <button
                    type="button"
                    key={sc.id}
                    className={`saved-card ${useSavedCard === sc.id ? 'on' : ''}`}
                    onClick={() => { setUseSavedCard(sc.id); setPayError(null); setCardLinkUrl(null); }}
                  >
                    <span className="sc-brand">{sc.brand}</span>
                    <span className="sc-digits">•••• {sc.last_digits}</span>
                    <span className="sc-exp">{String(sc.expiration_month).padStart(2, '0')}/{String(sc.expiration_year).slice(-2)}</span>
                    {sc.name && <span className="sc-name">{sc.name}</span>}
                  </button>
                ))}
                <button
                  type="button"
                  className={`saved-card new ${useSavedCard === null ? 'on' : ''}`}
                  onClick={() => {
                    setUseSavedCard(null); setPayError(null); setCardLinkUrl(null);
                    // Pre-fill billing fields from most recent saved card
                    var recent = savedCards[0];
                    if (recent) {
                      var nameParts = (recent.name || '').split(/\s+/);
                      setCard((prev) => ({
                        ...prev,
                        first: nameParts[0] || prev.first,
                        last: nameParts.slice(1).join(' ') || prev.last,
                        street: recent.street1 || prev.street,
                        city: recent.city || prev.city,
                        state: recent.state || prev.state,
                        postal: recent.postal_code || prev.postal,
                        country: (recent.country || prev.country).toUpperCase(),
                      }));
                    }
                  }}
                >
                  + New card
                </button>
              </div>
            )}

            {/* New card form — hidden when a saved card is selected */}
            {savedCardId === null && (
              <>
                <div className="cogrid">
                  <input className="coinput" placeholder="First name *" autoComplete="cc-given-name"
                    value={card.first} onChange={(e) => setCard({ ...card, first: e.target.value })} />
                  <input className="coinput" placeholder="Last name *" autoComplete="cc-family-name"
                    value={card.last} onChange={(e) => setCard({ ...card, last: e.target.value })} />
                </div>
                <select className="coinput" value={card.country} aria-label="Card country"
                  onChange={(e) => setCard({ ...card, country: e.target.value })}>
                  {COMMON_CARD_COUNTRIES.map((c) => <option key={c} value={c}>{countryName(c)}</option>)}
                  <option disabled>──────────</option>
                  {OTHER_CARD_COUNTRIES.map((c) => <option key={c} value={c}>{countryName(c)}</option>)}
                </select>

                {ADDRESS_COUNTRIES.includes(card.country) && (
                  <>
                    <input className="coinput" placeholder="Street address *" autoComplete="address-line1"
                      value={card.street} onChange={(e) => setCard({ ...card, street: e.target.value })} />
                    <div className="cogrid three">
                      <input className="coinput" placeholder="City *" autoComplete="address-level2"
                        value={card.city} onChange={(e) => setCard({ ...card, city: e.target.value })} />
                      {card.country === 'US' ? (
                        <select className="coinput" value={card.state} aria-label="State"
                          onChange={(e) => setCard({ ...card, state: e.target.value })}>
                          <option value="">State</option>
                          {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      ) : (
                        <input className="coinput" placeholder="County / Province" autoComplete="address-level1"
                          value={card.state} onChange={(e) => setCard({ ...card, state: e.target.value })} />
                      )}
                      <input className="coinput" placeholder="ZIP / Postcode *" autoComplete="postal-code"
                        value={card.postal} onChange={(e) => setCard({ ...card, postal: e.target.value })} />
                    </div>
                  </>
                )}

                <input className="coinput mono" placeholder="Card number" inputMode="numeric" autoComplete="cc-number"
                  value={card.number} onChange={(e) => setCard({ ...card, number: e.target.value })} />
                <div className="cogrid">
                  <input className="coinput mono" placeholder="MM/YY" inputMode="numeric" autoComplete="cc-exp"
                    value={card.exp} onChange={(e) => setCard({ ...card, exp: formatExpiry(e.target.value) })} />
                  <input className="coinput mono" placeholder="CVV" inputMode="numeric" autoComplete="cc-csc"
                    value={card.cvv} onChange={(e) => setCard({ ...card, cvv: e.target.value })} />
                </div>
                <div className="cohint">
                  Your card details go straight to Omise — they never touch our servers. Card payments
                  are verified with 3-D Secure, so expect a quick step at your bank.
                </div>
              </>
            )}
          </div>
        )}

        {/* ---------- place order ---------- */}
        <form className="cocard" onSubmit={placeOrder}>
          <label className="agree">
            <input type="checkbox" checked={agree} onChange={(e) => { setAgree(e.target.checked); try { if (e.target.checked) localStorage.setItem('mt_tos_agreed', '1'); else localStorage.removeItem('mt_tos_agreed'); } catch (ex) {} }} />
            <span>I agree to the <a href="/terms" target="_blank" rel="noopener">Terms &amp; Conditions</a> and <a href="/refund-policy" target="_blank" rel="noopener">Refund Policy</a>.</span>
          </label>
          {payError && (
            <div className="notice payerr">
              {payError}
              {payErrorMore && <> <a href={payErrorMore} target="_blank" rel="noopener">Learn more</a></>}
              {cardLinkUrl && (method === 'omise' || method === 'omise-tt') && (
                <div style={{ marginTop: 8 }}>
                  {cardLinkUrl === 'pending' ? (
                    <button type="button" className="btn block" style={{ background: '#2d6ca2', color: '#fff', marginTop: 4 }} onClick={requestCardLink} disabled={cardLinkBusy}>
                      {cardLinkBusy ? 'Creating payment link…' : 'Pay on secure Omise page'}
                    </button>
                  ) : (
                    <a href={cardLinkUrl} target="_blank" rel="noopener" className="btn block" style={{ background: '#2d6ca2', color: '#fff', display: 'block', textAlign: 'center', marginTop: 4 }}>
                      Open Omise payment page
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
          <div id="turnstile-checkout" style={{ margin: '10px 0' }} />
          <button type="submit" className="btn block colarge" disabled={placing || !quote}>
            {placing ? 'Placing your order…' : `Place order${selected ? ` · ${money(selected.total_thb, selected.display && selected.display.total)}` : ''}`}
          </button>
          <div className="paylogos">
            {method === 'xendit'
              ? PHP_PAYMENT_LOGOS.map((l) => (
                  <img key={l.file} src={`${ST}/images/payment-icons/${l.file}`} alt={l.name} title={l.name} />
                ))
              : ((METHOD_META[method] && (METHOD_META[method].paylogos || METHOD_META[method].logos)) || []).map((f) => (
                  <img key={f} src={`${ST}/images/payment-icons/${f}`} alt="" />
                ))}
          </div>
          <div className="cohint">
            {isEsim
              ? 'Your eSIM is prepared right after payment and the install details are emailed to you. Failed deliveries are refunded automatically.'
              : params.isGift
              ? 'Your eGift voucher code and redeem instructions will be emailed to you after payment. Failed deliveries are refunded automatically.'
              : 'Credit is delivered straight to the phone number above. Failed topups are refunded automatically.'}
          </div>
        </form>
      </div>

      <SiteFooterV2 currency={currency} onCurrency={onCurrency} />
    </div>
  );
}
